import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { auth, type User, type ImpersonatedBy } from './api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  impersonatedBy: ImpersonatedBy | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [impersonatedBy, setImpersonatedBy] = useState<ImpersonatedBy | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { user, impersonatedBy } = await auth.me();
      setUser(user);
      setImpersonatedBy(impersonatedBy);
    } catch {
      setUser(null);
      setImpersonatedBy(null);
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const { user } = await auth.login(email, password);
    setUser(user);
    setImpersonatedBy(null);
  };

  const logout = async () => {
    await auth.logout();
    setUser(null);
    setImpersonatedBy(null);
  };

  const stopImpersonating = async () => {
    const { user } = await auth.stopImpersonating();
    setUser(user);
    setImpersonatedBy(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, impersonatedBy, login, logout, refresh, stopImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
